<template>
  <div class="order-setting-page">
    <div class="setting-card" v-loading="loading">
      <div class="setting-header">
        <div>
          <h2>订单设置</h2>
          <p>统一配置商城订单的自动关闭、自动收货和售后有效期。</p>
        </div>
        <el-button type="primary" :loading="saving" @click="save">保存设置</el-button>
      </div>

      <el-form ref="form" :model="form" label-position="top" class="setting-form">
        <section class="setting-section">
          <div class="section-title-row">
            <div>
              <h3>待付款自动取消</h3>
              <p>订单提交后超过设定时间仍未付款，系统将自动关闭订单并释放库存。</p>
            </div>
            <el-switch v-model="form.autoCancelEnabled" />
          </div>
          <div class="inline-setting" :class="{ disabled: !form.autoCancelEnabled }">
            <span>提交订单后</span>
            <el-input-number
              v-model="form.unpaidCancelMinutes"
              :min="10"
              :max="1440"
              :controls="false"
              :disabled="!form.autoCancelEnabled"
            />
            <span>分钟未付款，自动取消订单</span>
          </div>
          <div class="setting-tip">可设置 10–1440 分钟</div>
        </section>

        <section class="setting-section">
          <div class="section-title-row">
            <div>
              <h3>自动确认收货</h3>
              <p>订单发货后超过设定天数仍未确认，系统将自动完成收货。</p>
            </div>
            <el-switch v-model="form.autoReceiveEnabled" />
          </div>
          <div class="inline-setting" :class="{ disabled: !form.autoReceiveEnabled }">
            <span>发货后</span>
            <el-input-number
              v-model="form.autoReceiveDays"
              :min="1"
              :max="30"
              :controls="false"
              :disabled="!form.autoReceiveEnabled"
            />
            <span>天，自动确认收货</span>
          </div>
          <div class="setting-tip">可设置 1–30 天，计算起点为订单发货时间</div>
        </section>

        <section class="setting-section">
          <div class="section-title-row">
            <div>
              <h3>确认收货后售后期限</h3>
              <p>超过有效期后，用户端不再展示“去售后”按钮，接口也会拒绝新申请。</p>
            </div>
            <el-switch v-model="form.afterSaleLimitEnabled" />
          </div>
          <div class="inline-setting" :class="{ disabled: !form.afterSaleLimitEnabled }">
            <span>确认收货后</span>
            <el-input-number
              v-model="form.afterSaleDays"
              :min="1"
              :max="365"
              :controls="false"
              :disabled="!form.afterSaleLimitEnabled"
            />
            <span>天内，可申请售后</span>
          </div>
          <div class="setting-tip">可设置 1–365 天；关闭限制后，已完成订单不限制售后申请时间</div>
        </section>
      </el-form>
    </div>
  </div>
</template>

<script>
import { getOrderConfig, saveOrderConfig } from "@/api/order";

export default {
  name: "OrderSetting",
  data() {
    return {
      loading: false,
      saving: false,
      form: {
        autoCancelEnabled: true,
        unpaidCancelMinutes: 60,
        autoReceiveEnabled: true,
        autoReceiveDays: 7,
        afterSaleLimitEnabled: true,
        afterSaleDays: 60
      }
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await getOrderConfig();
        if (res && res.bizcode === 100 && res.data) {
          this.form = { ...this.form, ...res.data };
        }
      } finally {
        this.loading = false;
      }
    },
    validate() {
      const { unpaidCancelMinutes, autoReceiveDays, afterSaleDays } = this.form;
      if (unpaidCancelMinutes < 10 || unpaidCancelMinutes > 1440)
        return "待付款取消时间须在 10–1440 分钟之间";
      if (autoReceiveDays < 1 || autoReceiveDays > 30) return "自动确认收货时间须在 1–30 天之间";
      if (afterSaleDays < 1 || afterSaleDays > 365) return "售后期限须在 1–365 天之间";
      return "";
    },
    async save() {
      const message = this.validate();
      if (message) {
        this.$message.warning(message);
        return;
      }
      this.saving = true;
      try {
        const res = await saveOrderConfig(this.form);
        if (res && res.bizcode === 100) {
          this.form = { ...this.form, ...res.data };
          this.$message.success("保存成功");
        } else {
          this.$message.error((res && res.msg) || "保存失败");
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.order-setting-page {
  min-height: calc(100vh - 110px);
  padding: 24px;
  background: #f5f7fa;
}

.setting-card {
  max-width: 1120px;
  min-height: 620px;
  margin: 0 auto;
  padding: 28px 32px 40px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(31, 45, 61, 0.06);
}

.setting-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 22px;
  border-bottom: 1px solid #ebeef5;

  h2 {
    margin: 0 0 8px;
    color: #1f2d3d;
    font-size: 22px;
  }
  p {
    margin: 0;
    color: #909399;
    font-size: 14px;
  }
}

.setting-form {
  max-width: 820px;
}

.setting-section {
  padding: 28px 0;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: 0;
  }
}

.section-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;

  h3 {
    margin: 0 0 8px;
    color: #303133;
    font-size: 17px;
    font-weight: 600;
  }
  p {
    margin: 0;
    color: #909399;
    font-size: 14px;
    line-height: 1.6;
  }
}

.inline-setting {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 22px;
  color: #303133;

  &.disabled {
    color: #c0c4cc;
  }
  ::v-deep .el-input-number {
    width: 120px;
  }
  ::v-deep .el-input__inner {
    text-align: left;
  }
}

.setting-tip {
  margin: 10px 0 0 84px;
  color: #a8abb2;
  font-size: 13px;
}
</style>
